import { getPageUrl, getLastPageNumber } from './productSearch';
import getFrontConfig from '../../common/getFrontConfig';

import { urlWithSearchParams } from '../../common/utils/url';

/**
 * Search related data. It has more properties than defined in this type definition.
 * @typedef {object} ProductSearch
 * @property {object} category - Current category data (id, name, pageDescription, pageKeywords,
 * pageTitle)
 * @property {number} count - total results
 * @property {boolean} isCategorySearch
 * @property {boolean} isRefinedCategorySearch
 * @property {boolean} isSearchSuggestionsAvailable
 * @property {number} pageNumber - search results page number
 * @property {number} pageSize - results by page
 * @property {string} permalink - URL to this page search results
 * @property {object[]} products - products array returned by the search
 */

function createProductSearchPrefetcher() {
  let adjacentProductSearchPromise = null;
  const instance = {
    /**
     * Gets from cache a product search
     * @param {string} productSearchUrl - product search page url
     * @param {boolean} prefetchAdjacent - product search page url
     * @returns {ProductSearch} product search
     */
    get(productSearchUrl, prefetchAdjacent = true) {
      if (productSearchUrl === null) {
        return Promise.reject(new Error('URL is null'));
      }

      if (adjacentProductSearchPromise) {
        return adjacentProductSearchPromise.finally(() => {
          adjacentProductSearchPromise = null;
        });
      }
      const urlSearchParams = {
        format: 'json',
        platform: 'web',
      };
      const {
        searchAI: { isFrontTestEnabled = false },
      } = getFrontConfig();
      if (isFrontTestEnabled) {
        urlSearchParams.isFrontTestEnabled = isFrontTestEnabled;
      }
      return fetch(urlWithSearchParams(productSearchUrl, urlSearchParams))
        .then((res) => res.json())
        .then(({ productSearch }) => {
          if (
            prefetchAdjacent &&
            getLastPageNumber(productSearch) >= productSearch.pageNumber + 1
          ) {
            const adjacentPageUrl = getPageUrl(
              productSearch,
              productSearch.pageNumber + 1
            );
            adjacentProductSearchPromise = this.get(adjacentPageUrl, false);
          }
          return productSearch;
        });
    },

    reset(productSearch) {
      // eslint-disable-next-line max-len
      const initialPageNumber =
        productSearch?.pageNumber !== undefined ? productSearch.pageNumber : -1;
      if (initialPageNumber > -1) {
        this.get(getPageUrl(productSearch, initialPageNumber));
      }
    },
  };

  // instance.reset(initialProductSearch);

  return instance;
}

export {
  // eslint-disable-next-line import/prefer-default-export
  createProductSearchPrefetcher,
};
